import { PaletteMode } from '@mui/material';

import * as lightColors from './colors';
import {
    darkCement,
    darkGreen,
    darkGrey,
    darkInk,
    darkOnColor,
    darkOrange,
    darkPurple,
    darkRed,
    darkSurface,
    darkText,
} from './darkColors';
import { croissantColors } from './croissantColors';

const { partnerOrange, brandPurple } = lightColors;

/* Light keeps the original values; `ink` and `onColor` stand in for black/white wherever the palette meant
   "strongest foreground" and "text on a solid fill", which swap in dark. */
const modeColors = {
    light: {
        purple: lightColors.purple,
        red: lightColors.red,
        orange: lightColors.orange,
        green: lightColors.green,
        grey: lightColors.grey,
        cement: lightColors.cement,
        ink: lightColors.black,
        onColor: lightColors.white,
        surface: { default: lightColors.white, paper: lightColors.white },
        /* Not `grey[500]` (#787878), which measured 4.42:1 on white and 3.98:1 on the cement tints —
           under WCAG AA both times, by a hair on white and clearly on a filled surface. This is two
           steps darker and clears 5.01:1 on white and 4.52:1 on cement.100, which are the two
           grounds secondary text actually sits on. Off-ramp on purpose: the ramp itself is used for
           borders and fills whose contrast is unaffected, so darkening grey[500] would move far more
           than the text it needs to fix. */
        text: { secondary: '#706F6F', disabled: lightColors.grey[300] },
    },
    dark: {
        purple: darkPurple,
        red: darkRed,
        orange: darkOrange,
        green: darkGreen,
        grey: darkGrey,
        cement: darkCement,
        ink: darkInk,
        onColor: darkOnColor,
        surface: darkSurface,
        text: darkText,
    },
};

export function createPalette(mode: PaletteMode) {
    const { purple, red, orange, green, grey, cement, ink, onColor, surface, text } = modeColors[mode];

    return {
        // MUI palette options
        mode,
        /** Strongest foreground: black in light, near-white in dark. */
        ink,
        /** Text on a solid accent fill: white in light, near-black in dark. */
        onColor,
        primary: {
            main: purple[500],
            dark: purple[700],
            hover: purple[400],
            lightHover: purple[100],
            light: purple[300],
            contrastText: onColor,
        },
        lightPrimary: {
            main: purple[100],
            dark: purple[200],
            hover: purple[50],
            lightHover: purple[50],
            light: purple[50],
            contrastText: purple[500],
        },
        secondary: {
            main: ink,
            dark: grey[700],
            hover: grey[600],
            lightHover: cement[100],
            light: grey[500],
            contrastText: onColor,
        },
        lightSecondary: {
            main: grey[50],
            dark: grey[100],
            light: surface.paper,
            contrastText: ink,
        },
        error: {
            main: red[500],
            dark: red[900],
            hover: red[400],
            lightHover: red[100],
            light: red[300],
            contrastText: onColor,
        },
        warning: {
            main: orange[500],
            dark: orange[700],
            hover: orange[400],
            lightHover: orange[100],
            light: orange[300],
            contrastText: onColor,
        },
        info: {
            main: cement[500],
            dark: cement[900],
            hover: cement[400],
            lightHover: cement[100],
            light: cement[200],
            contrastText: onColor,
        },
        success: {
            main: green[500],
            dark: green[700],
            hover: green[400],
            lightHover: green[100],
            light: green[300],
            contrastText: onColor,
        },
        lightSuccess: {
            main: green[300],
            dark: green[600],
            hover: green[200],
            lightHover: green[50],
            light: green[100],
            contrastText: onColor,
        },
        text: {
            primary: grey[900],
            secondary: text.secondary,
            disabled: text.disabled,
        },
        action: {
            active: ink,
            hover: grey[50],
            selected: grey[100],
            disabled: grey[300],
            disabledBackground: grey[50],
            focus: grey[400],
        },
        background: {
            default: surface.default,
            paper: surface.paper,
        },
        divider: cement[200],
        common: {
            black: lightColors.black,
            white: lightColors.white,
        },
        // Custom palette options
        purple: {
            ...purple,
            main: purple[500],
            dark: purple[700],
            light: purple[300],
            contrastText: onColor,
        },
        red: {
            ...red,
            main: red[500],
            dark: red[700],
            light: red[300],
            contrastText: onColor,
        },
        orange: {
            ...orange,
            main: orange[500],
            dark: orange[700],
            light: orange[300],
            contrastText: onColor,
        },
        partnerOrange: {
            ...partnerOrange,
            contrastText: onColor,
        },
        brandPurple: {
            ...brandPurple,
            // The track behind a brandPurple LinearProgress, which reads `lightHover` for it.
            lightHover: grey[50],
            contrastText: onColor,
        },
        green: {
            ...green,
            main: green[500],
            dark: green[700],
            light: green[300],
            contrastText: onColor,
        },
        grey: {
            ...grey,
            main: grey[500],
            dark: grey[700],
            light: grey[300],
            contrastText: onColor,
        },
        lightGrey: {
            ...grey,
            main: grey[50],
            hover: grey[100],
            dark: grey[200],
            light: grey[300],
            contrastText: grey[900],
        },
        neutral: {
            main: grey[200],
            dark: grey[400],
            hover: grey[300],
            lightHover: grey[50],
            light: grey[100],
            contrastText: grey[900],
        },
        onyx: {
            main: ink,
            dark: ink,
            hover: grey[800],
            lightHover: grey[100],
            light: grey[800],
            contrastText: onColor,
        },
        cement: {
            ...cement,
            main: cement[50],
            hover: cement[100],
        },
        inProgress: {
            main: green[700],
            light: green[50],
            lightHover: green[100],
            contrastText: green[500],
        },
        important: {
            main: purple[500],
            dark: purple[900],
            lightHover: purple[100],
            light: purple[300],
            contrastText: onColor,
        },
        shades: {
            primary: {
                '4p': `${purple[500]}0A`,
                '8p': `${purple[500]}14`,
                '12p': `${purple[500]}1F`,
                '30p': `${purple[500]}4D`,
                '50p': `${purple[500]}80`,
            },
            secondary: {
                '4p': grey[50],
                '8p': grey[50],
                '12p': grey[100],
                '30p': grey[100],
                '50p': grey[200],
            },
            error: {
                '4p': `${red[500]}0A`,
                '12p': `${red[500]}4D`,
                '30p': `${red[500]}80`,
                '50p': `${purple[500]}80`,
                '160p': ink,
                '190p': red[100],
            },
            warning: {
                '4p': `${orange[500]}0A`,
                '12p': `${orange[500]}1F`,
                '30p': `${orange[500]}4D`,
                '50p': `${orange[500]}80`,
                '160p': ink,
                '190p': orange[100],
            },
            info: {
                '4p': `${purple[500]}0A`,
                '12p': `${purple[500]}1F`,
                '30p': `${purple[500]}4D`,
                '50p': `${purple[500]}80`,
                '160p': ink,
                '190p': purple[100],
            },
            success: {
                '4p': `${green[500]}0A`,
                '12p': `${green[500]}1F`,
                '30p': `${green[500]}4D`,
                '50p': `${green[500]}80`,
                '160p': ink,
                '190p': green[100],
            },
        },
        croissant: croissantColors,
        croissantNeutral: {
            main: croissantColors.background.neutral.bold,
            dark: croissantColors.background.neutral.boldPressed,
            hover: croissantColors.background.neutral.boldHovered,
            lightHover: croissantColors.background.neutral.subtleHovered,
            light: croissantColors.background.neutral.subtle,
            contrastText: croissantColors.text.inverse.primary,
            pressed: croissantColors.background.neutral.boldPressed,
            disabled: croissantColors.background.neutral.boldDisabled,
            disabledText: croissantColors.text.inverse.primaryDisabled,
        },
    } as const;
}

export type AppPalette = ReturnType<typeof createPalette>;
